<template>
  <header class="sidebar-header">
    <div class="sidebar-header-mobile">
      <span class="menu-name">Menu</span>
      <p-icon icon-name="close"
              size="large"
              color="white" 
              @click="menuStore.toggleMenu()"/>
    </div>
    <pidz-logo class="logo" />
    <div class="profile">
      <div class="user" @click="toggleMenuState">
        <p-badge v-if="showProfileBadge"
                 class="profile__badge"
                 variant="warn"
                 content="!"
                 :bordered="true"/>
        <p-icon class="icon"
                icon-name="profile"
                size="large"
                color="white" />
        <span class="name">{{ userName }}</span>
        <p-icon :class="['chevron', { open: isMenuOpened }]" 
                icon-name="chevron"
                color="white" 
                size="small" />
      </div>
      <transition>
        <nav v-if="isMenuOpened" ref="navigation"
             class="profile-nav">
          <a v-if="userStore.isImpersonated"
             class="menu-item"
             @click="backToAdmin">Terug naar admin</a>
          <template v-for="(route, _i) in menuItems" :key="_i">
            <span style="display: block; position: relative">
              <hybrid-link v-if="!hiddenForUser(route?.hiddenFromAdmin)"
                           class="menu-item"
                           :to="route.to">{{ route.name }}</hybrid-link>
              <p-badge v-if="(route.name === 'Profiel' || route.name === 'Instellingen') && showProfileBadge"
                       class="profile__menu-badge"
                       variant="warn"
                       content="!"
                       :bordered="false"/>
            </span>
          </template>
          <a href="#" class="menu-item"
             @click.prevent="handleLogout">Uitloggen</a>
        </nav>
      </transition>
    </div>
    <div class="divider" />
  </header>
</template>
<script lang="ts" setup>
import { authService } from '@pidz/api';
import { useFeatureflagsStore, useMenuStore, useUserStore, useBannersStore } from '@pidz/stores';
import HybridLink from '@web/components/HybridLink.vue';
import PidzLogo from '@web/components/PidzLogo.vue';
import { router } from '@web/router';
import Cookies from 'js-cookie';
import { computed, ref } from 'vue';
import { redirectToLogin } from '../../utils/webAuth';
import { MenuItem } from './types/types';

const menuStore = useMenuStore();
const bannersStore = useBannersStore();
const featureFlagsStore = useFeatureflagsStore();

const isMenuOpened = ref(false);
const navigation = ref<HTMLElement | null>(null);
const userStore = useUserStore();
const authError = ref<string|null>(null);
const old_ffe: string = import.meta.env.VITE_FFE_OLD_URL;

const toggleMenuState = () => {
  isMenuOpened.value = !isMenuOpened.value;
};

const hiddenForUser = (hidden: boolean | undefined) => {
  if (hidden === undefined) {
    return false;
  }
  if (hidden && !userStore.isImpersonated) {
    return false;
  }
  return true;
};

const userName = computed(() => {
  if (!userStore.user) {
    userStore.getCurrentUser();
    return '';
  }
  const { first_name, insertion, last_name } = userStore.user;
  return `${first_name} ${insertion ?? ''} ${last_name}`;
});

const backToAdmin = () => {
  Cookies.remove('pidz_rpc_assume_user');
  window.location.href = import.meta.env.VITE_BACKEND_HOST;
};

const showPreferences = computed(() => featureFlagsStore.getStatus('ffe-preferences'));
const showProfileBadge = computed(() => !!bannersStore.getBannerPerRoute('profile')?.length);

const menuItems = computed<Array<{ hiddenFromAdmin?: boolean } & MenuItem>>(() => {
  const items = [{
    name: 'Profiel',
    to: `/settings/profile`,
  },
  {
    name: 'Voorkeuren',
    to: `${old_ffe}/preferences`,
  },
  {
    name: 'Wachtwoord wijzigen',
    to: `${old_ffe}/reset-password`,
    hiddenFromAdmin: true,
  }]

  if (showPreferences.value) {
    items.splice(0, 2, {
      name: 'Instellingen',
      to: '/settings/profile',
    });
  }

  return items;
});

const handleLogout = async () => {
  try {
    await authService.logout();
    router.replace({ name: 'dashboard' });
    redirectToLogin();
  } catch (e) {
    authError.value = 'Er ging iets mis bij het uitloggen, mogelijk helpt het om de pagina te verversen.';
  }
};
</script>
<style lang="scss" scoped>
.sidebar-header {
  height: auto;
  padding-top: 32px;
  background-color: var(--pidz-blue);

  a {
    color: inherit;
    font-weight: inherit;
    text-decoration: inherit;
    line-height: 1;
  }

  .logo {
    display: block;
    margin: auto;
    height: 27px;
  }

  .profile {
    margin-top: 48px;

    &__badge{
      position: absolute; 
      z-index: 100;
      left: 45px; 
      top: -5px;
    }

    &__menu-badge{
      position: absolute; 
      z-index: 100;
      left: 15px; 
      top: 0px;
    }

    .user {
      padding: 0 32px;
      width: 100%;
      display: flex;
      align-items: center;
      margin-bottom: 8px;
      cursor: pointer;
      position: relative;

      .icon {
        flex-shrink: 0;
      }

      .name {
        margin: 0 8px;
        width: 100%;
        text-align: center;
        word-break: break-word;
        overflow-x: hidden;
      }

      .chevron {
        left: 100%;
        right: 0;
        transition: all 0.2s ease-out;
        transform: rotate(180deg);

        &.open {
          transform: rotate(360deg);
        }
      }
    }

    nav {
      a {
        padding: 16px 32px;
        display: block;
      }
    }
  }

  .divider {
    border-top: 1px solid white;
    height: 1px;
    margin: 16px 32px 0 32px;
    color: white;
  }

  .v-enter-active,
  .v-leave-active {
    max-height: 275px;
    transition: max-height 0.33s ease-out;
    overflow: hidden;
  }

  .v-enter-from,
  .v-leave-to {
    max-height: 0;
  }
}
</style>
